import { GiftItemBrand } from '@shared/api/giftItemBrand/giftItemBrand.interface';
import { GiftItemInfoType } from '@shared/api/giftItemInfo/giftItemInfo.interface';

/** 추가금 설정을 들고 있는 최소 형태 (원상품 정보 + populated 브랜드) */
interface SurchargeResolvable {
  deliveryDetail?: {
    shippingFee?: number;
    freeShippingMinQuantity?: number;
  };
  giftItemBrand?: GiftItemBrand | string;
}

/**
 * 소량주문 추가금 설정 판정 — 상품값이 브랜드값을 오버라이드.
 * shippingFee(개당 추가금) / freeShippingMinQuantity(면제 최소수량)를 각각 상품 → 브랜드 순으로 결정.
 */
export const resolveShippingFeeConfig = (
  info?: SurchargeResolvable | null,
): { shippingFee?: number; freeShippingMinQuantity?: number } => {
  if (!info) return {};
  const brand = typeof info.giftItemBrand === 'object' ? info.giftItemBrand : undefined;
  return {
    shippingFee: info.deliveryDetail?.shippingFee ?? brand?.deliveryDetail?.shippingFee,
    freeShippingMinQuantity:
      info.deliveryDetail?.freeShippingMinQuantity ??
      brand?.deliveryDetail?.freeShippingMinQuantity,
  };
};

/**
 * 개당 추가금 — 배송비가 아니라 옵션처럼 단가에 더해지는 소량주문 할증.
 * - shippingFee 미설정/0 → 0
 * - freeShippingMinQuantity 이상 주문 → 0 (면제)
 * - 그 미만 → shippingFee (수량 1개당 가산액)
 */
export const getUnitSurcharge = (
  info: SurchargeResolvable | null | undefined,
  quantity: number,
): number => {
  const { shippingFee, freeShippingMinQuantity } = resolveShippingFeeConfig(info);
  if (!shippingFee || shippingFee <= 0) return 0;
  if (freeShippingMinQuantity != null && quantity >= freeShippingMinQuantity) return 0;
  return shippingFee;
};

/** 추가금 계산용 주문 라인 */
interface SurchargeLine {
  giftItemInfo?:
    | (SurchargeResolvable & { isTaxable?: boolean; type?: GiftItemInfoType })
    | null;
  quantity: number;
}

export interface OrderSurcharge {
  total: number;
  taxable: number; // 과세 상품 추가금 합 (부가세 포함 금액)
  taxFree: number; // 면세 상품 추가금 합
}

/**
 * 주문 전체 소량주문 추가금 — 라인별로 (개당 추가금 × 수량)을 합산하고,
 * 상품의 과세여부에 맞춰 과세/면세로 나눠 집계. 쿠폰형은 제외.
 */
export const calcOrderSurcharge = (lines: SurchargeLine[]): OrderSurcharge => {
  let taxable = 0;
  let taxFree = 0;

  for (const line of lines) {
    const info = line.giftItemInfo;
    if (!info || info.type === GiftItemInfoType.Coupon) continue; // 쿠폰형은 추가금 없음
    const amount = getUnitSurcharge(info, line.quantity) * line.quantity;
    if (amount <= 0) continue;
    if (info.isTaxable) taxable += amount;
    else taxFree += amount;
  }

  return { total: taxable + taxFree, taxable, taxFree };
};
